/**
 * File upload router
 * @package @app/api
 *
 * SECURITY: The previous `register` procedure accepted a client-supplied
 * blob URL and validated only the hostname. This allowed User B to
 * register User A's blob URL on a deal that User B owned, then delete it
 * — wiping User A's actual blob storage.
 *
 * The fix: file records are now created exclusively server-side by the
 * `/api/upload` route's `onUploadCompleted` webhook, which receives a
 * Vercel-signed `tokenPayload` containing the validated userId and dealId.
 * Clients can no longer supply URLs.
 */

import { z } from 'zod';
import { protectedProcedure } from '../middleware/auth';

export const fileRouter = {
  delete: protectedProcedure
    .input(z.object({ fileId: z.string() }))
    .handler(async ({ context, input }) => {
      const { db } = context;
      const userId = context.session.user.id;

      // Find the file owned by this user
      const file = await db.fileUpload.findFirst({
        where: { id: input.fileId, userId },
      });
      if (!file) throw new Error('File not found');

      // Defense in depth: verify URL is a Vercel Blob URL before deletion.
      // Files are only ever created via the signed webhook, but we double-
      // check here so a future bug in the create path can't escalate into
      // arbitrary URL deletion.
      let url: URL;
      try {
        url = new URL(file.url);
      } catch {
        throw new Error('Invalid file URL');
      }
      if (!url.hostname.endsWith('.public.blob.vercel-storage.com')) {
        throw new Error('Invalid file URL');
      }

      const { del } = await import('@vercel/blob');
      await del(file.url);
      await db.fileUpload.delete({ where: { id: input.fileId } });

      return { success: true };
    }),
};
